import { LitElement, html, css } from 'lit';
import { store } from '../lib/store.js';
import { router } from '../lib/router.js';

export class SiteHeader extends LitElement {
  static properties = {
    _menuOpen: { state: true },
    _scrolled: { state: true },
    _pages: { state: true },
    _currentSlug: { state: true },
    _settings: { state: true },
  };

  static styles = css`
    :host {
      display: block;
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      z-index: 100;
      transition: background var(--transition-base, 0.25s ease);
      color: var(--color-text-primary);
    }
    :host(.scrolled) {
      background: var(--color-bg-glass);
      backdrop-filter: blur(20px);
      -webkit-backdrop-filter: blur(20px);
      border-bottom: 1px solid var(--color-border);
      box-shadow: 0 10px 30px color-mix(in srgb, var(--color-text-primary) 8%, transparent);
    }
    .header-inner {
      max-width: var(--content-max-width, 1440px);
      margin: 0 auto;
      padding: 0 var(--content-padding, 2rem);
      height: var(--header-height, 4rem);
      display: flex;
      align-items: center;
      justify-content: space-between;
    }
    .logo {
      display: flex;
      align-items: center;
      gap: var(--space-3, 0.75rem);
      text-decoration: none;
      cursor: pointer;
    }
    .logo-icon {
      width: 3.25rem;
      height: 2.35rem;
      background: var(--color-accent);
      border-radius: 0.5rem;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 800;
      font-size: 1.35rem;
      font-style: italic;
      color: white;
      letter-spacing: 0;
      box-shadow: 0 10px 22px color-mix(in srgb, var(--color-accent) 20%, transparent);
    }
    .logo-text {
      font-weight: var(--font-medium, 500);
      font-size: var(--text-sm, 0.875rem);
      color: var(--color-text-primary, #f0f0f5);
      letter-spacing: 0;
    }
    .logo-text { color: var(--color-text-primary); }
    nav {
      display: flex;
      align-items: center;
      gap: var(--space-1, 0.25rem);
    }
    .nav-link {
      padding: var(--space-2, 0.5rem) var(--space-4, 1rem);
      border-radius: var(--border-radius, 0.75rem);
      color: var(--color-text-secondary, #a0a0b8);
      font-size: var(--text-sm, 0.875rem);
      font-weight: var(--font-medium, 500);
      text-decoration: none;
      transition: all var(--transition-fast, 0.15s ease);
      cursor: pointer;
    }
    .nav-link { color: var(--color-text-primary); }
    .nav-link:hover {
      color: var(--color-accent);
      background: var(--color-surface);
    }
    .nav-link--active {
      color: var(--color-accent, #00d4ff);
      background: var(--color-accent-dim, rgba(0, 212, 255, 0.15));
    }
    .nav-link--signin {
      background: var(--color-surface);
      color: var(--color-accent);
      margin-left: var(--space-2, 0.5rem);
      border: 1px solid var(--color-border);
      box-shadow: 0 8px 22px color-mix(in srgb, var(--color-text-primary) 12%, transparent);
    }
    .nav-link--signin:hover {
      background: white;
      color: var(--color-accent-hover);
      transform: translateY(-1px);
    }
    .hamburger {
      display: none;
      flex-direction: column;
      gap: 5px;
      padding: var(--space-2, 0.5rem);
      background: none;
      border: none;
      cursor: pointer;
    }
    .hamburger span {
      display: block;
      width: 24px;
      height: 2px;
      background: var(--color-text-primary, #f0f0f5);
      border-radius: 2px;
      transition: all var(--transition-fast, 0.15s ease);
    }
    .hamburger span { background: var(--color-text-primary); }
    .hamburger.open span:nth-child(1) { transform: translateY(7px) rotate(45deg); }
    .hamburger.open span:nth-child(2) { opacity: 0; }
    .hamburger.open span:nth-child(3) { transform: translateY(-7px) rotate(-45deg); }

    @media (max-width: 768px) {
      .hamburger { display: flex; }
      nav {
        display: none;
        position: absolute;
        top: var(--header-height, 4rem);
        left: 0;
        right: 0;
        background: var(--color-bg-glass);
        backdrop-filter: blur(20px);
        flex-direction: column;
        padding: var(--space-4, 1rem);
        border-bottom: 1px solid var(--color-border);
        gap: var(--space-1, 0.25rem);
      }
      nav.open {
        display: flex;
      }
      .nav-link {
        width: 100%;
        padding: var(--space-3, 0.75rem) var(--space-4, 1rem);
      }
      .nav-link--signin {
        margin-left: 0;
        text-align: center;
      }
    }
  `;

  constructor() {
    super();
    this._menuOpen = false;
    this._scrolled = false;
    this._pages = [];
    this._currentSlug = router.currentSlug || 'home';
    this._settings = null;

    this._onScroll = () => {
      this._scrolled = window.scrollY > 50;
      this.classList.toggle('scrolled', this._scrolled);
      this.requestUpdate();
    };
    this._onStoreUpdate = () => {
      this._pages = store.getNavPages();
      this._settings = store.getSiteSettings();
      this.requestUpdate();
    };
    this._onRouteChange = (e) => {
      this._currentSlug = e.detail.slug || 'home';
      this.classList.add('light');
      this.requestUpdate();
    };
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('scroll', this._onScroll, { passive: true });
    router.addEventListener('route-changed', this._onRouteChange);
    this._unsub = store.subscribe(this._onStoreUpdate);
    this._pages = store.getNavPages();
    this._settings = store.getSiteSettings();
    this.classList.add('light');
    this.classList.toggle('scrolled', window.scrollY > 50);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('scroll', this._onScroll);
    router.removeEventListener('route-changed', this._onRouteChange);
    if (this._unsub) this._unsub();
  }

  _navigate(slug) {
    router.navigate(slug);
    this._menuOpen = false;
    this.requestUpdate();
  }

  _toggleMenu() {
    this._menuOpen = !this._menuOpen;
    this.requestUpdate();
  }

  _isActive(slug) {
    const current = router.currentSlug || 'home';
    return slug === current;
  }

  render() {
    const navPages = this._pages.filter(p => p.slug !== 'signin' && p.slug !== 'signup');
    const brand = this._settings?.brand || {};
    const header = this._settings?.header || {};

    return html`
      <div class="header-inner">
        <a class="logo" href=${router.pathFor('home')} @click=${(e) => { e.preventDefault(); this._navigate('home'); }}>
          <div class="logo-icon">${brand.logo_text || ''}</div>
          <span class="logo-text">${brand.name || ''}</span>
        </a>

        <button
          class="hamburger ${this._menuOpen ? 'open' : ''}"
          @click=${this._toggleMenu}
          aria-label=${header.menu_toggle_label || ''}
        >
          <span></span><span></span><span></span>
        </button>

        <nav class=${this._menuOpen ? 'open' : ''}>
          ${navPages.map(p => html`
            <a class="nav-link ${this._isActive(p.slug) ? 'nav-link--active' : ''}"
               href=${router.pathFor(p.slug)}
               @click=${(e) => { e.preventDefault(); this._navigate(p.slug); }}>
              ${p.title}
            </a>
          `)}
          <a class="nav-link nav-link--signin"
             href=${router.pathFor(header.sign_in_slug || 'signin')}
             @click=${(e) => { e.preventDefault(); this._navigate(header.sign_in_slug || 'signin'); }}>
            ${header.sign_in_label || ''}
          </a>
        </nav>
      </div>
    `;
  }
}

customElements.define('site-header', SiteHeader);
